'use client';

import { AssistantRuntimeProvider } from '@assistant-ui/react';
import { useChatRuntime, AssistantChatTransport } from '@assistant-ui/react-ai-sdk';
import { type UIMessage } from 'ai';
import { useSearchParams } from 'next/navigation';
import { type JSX, type ReactNode, useMemo } from 'react';
import { type ConversationalSearchContentClass, SearchUrlParamKeys } from 'search-gateway-client';

import { stripContentClassPrefix, withContentClassPrefix } from '../../_utils/contentClassPrefix';

interface ChatRuntimeProviderProps {
    children: ReactNode;
    initialMessages?: UIMessage[];
    supportsAudio?: boolean;
}

function parseContentClass(value: string | null): ConversationalSearchContentClass | undefined {
    return value === 'audio' || value === 'video' ? value : undefined;
}

export function ChatRuntimeProvider({
    children,
    initialMessages,
    supportsAudio = false,
}: ChatRuntimeProviderProps): JSX.Element {
    // The content class the search was initiated from (e.g. audio vs video page), carried on the
    // `/ai/conv-search/new` URL. We prepend it to the first message's text so the LLM defaults to
    // that modality — without relying on a structured backend parameter. The prefix is stripped
    // from display (see stripContentClassPrefix) so the user never sees it, including on reload.
    const contentClass = parseContentClass(useSearchParams().get(SearchUrlParamKeys.CONTENT_CLASS));
    const transport = useMemo(
        () =>
            new AssistantChatTransport({
                api: '/api/conv-search/chat',
                prepareSendMessagesRequest: ({ messages, body, ...rest }) => {
                    const lastAssistant = messages.findLast((m: UIMessage) => m.role === 'assistant');
                    const metadata = lastAssistant?.metadata as Record<string, Record<string, unknown>> | undefined;
                    const sessionId = metadata?.custom?.sessionId as string | undefined;
                    const lastUserMessage = messages.findLast((m: UIMessage) => m.role === 'user');
                    // Only bias the first message of a new search — once a sessionId exists, follow-ups
                    // continue naturally from conversation context.
                    const outgoingUserMessage =
                        lastUserMessage && !sessionId && contentClass
                            ? withContentClassPrefix(lastUserMessage, contentClass)
                            : lastUserMessage;
                    return {
                        body: {
                            ...body,
                            ...rest,
                            ...(outgoingUserMessage ? { messages: [outgoingUserMessage] } : {}),
                            ...(sessionId && { sessionId }),
                            permissions: { audio: supportsAudio, cross_asset_pairing: supportsAudio },
                        },
                    };
                },
            }),
        [supportsAudio, contentClass],
    );
    const displayMessages = useMemo(() => initialMessages?.map(stripContentClassPrefix), [initialMessages]);
    const runtime = useChatRuntime({ transport, messages: displayMessages });

    return <AssistantRuntimeProvider runtime={runtime}>{children}</AssistantRuntimeProvider>;
}
